import React, { useState, useEffect } from 'react';
import pickBy from 'lodash/pickBy';
import classNames from 'classnames';

import { manualTransactions } from '../../../util/api';

import appSettings from '../../../config/settings';
import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { isMainSearchTypeKeywords, isOriginInUse } from '../../../util/search';
import { parse, stringify } from '../../../util/urlHelpers';
import { createResourceLocatorString, matchPathname, pathByRouteName } from '../../../util/routes';
import {
  Button,
  LimitedAccessBanner,
  LinkedLogo,
  Modal,
  ModalMissingInformation,
  NamedLink,
} from '../../../components';
import { getSearchPageResourceLocatorStringParams } from '../../SearchPage/SearchPage.shared';

import MenuIcon from './MenuIcon';
import SearchIcon from './SearchIcon';
import TopbarSearchForm from './TopbarSearchForm/TopbarSearchForm';
import TopbarMobileMenu from './TopbarMobileMenu/TopbarMobileMenu';
import TopbarDesktop from './TopbarDesktop/TopbarDesktop';
import {
  BellOutlined, RightOutlined, CheckCircleOutlined, UserAddOutlined
} from '@ant-design/icons';
import { Drawer, List, Badge, Typography, message } from 'antd';

const { Text, Title } = Typography;

// Some requests report a refusal by resolving to false, others by answering success: false.
const isAnswered = result => !!result && result.success !== false;

import css from './Topbar.module.css';
import { userTypeOf } from '../../../util/userType';
import { employerAnswerNoticeOf, employerAnswerNoticeText } from '../../../util/employerAnswerNotice';
import NoticeContent, { unseenNotices } from './NoticeContent';

const MAX_MOBILE_SCREEN_WIDTH = 1024;

const SEARCH_DISPLAY_ALWAYS = 'always';
const SEARCH_DISPLAY_NOT_LANDING_PAGE = 'notLandingPage';
const SEARCH_DISPLAY_ONLY_SEARCH_PAGE = 'onlySearchPage';

const redirectToURLWithModalState = (history, location, modalStateParam) => {
  const { pathname, search, state } = location;
  const searchString = `?${stringify({ [modalStateParam]: 'open', ...parse(search) })}`;
  history.push(`${pathname}${searchString}`, state);
};

const redirectToURLWithoutModalState = (history, location, modalStateParam) => {
  const { pathname, search, state } = location;
  const queryParams = pickBy(parse(search), (v, k) => {
    return k !== modalStateParam;
  });
  const stringified = stringify(queryParams);
  const searchString = stringified ? `?${stringified}` : '';
  history.push(`${pathname}${searchString}`, state);
};

const isPrimary = o => o.group === 'primary';
const isSecondary = o => o.group === 'secondary';
const compareGroups = (a, b) => {
  const isAHigherGroupThanB = isPrimary(a) && isSecondary(b);
  const isALesserGroupThanB = isSecondary(a) && isPrimary(b);
  // Note: sort order is stable in JS
  return isAHigherGroupThanB ? -1 : isALesserGroupThanB ? 1 : 0;
};
// Returns links in order where primary links are returned first
const sortCustomLinks = customLinks => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.sort(compareGroups);
};

// Resolves in-app links against route configuration
const getResolvedCustomLinks = (customLinks, routeConfiguration) => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.map(linkConfig => {
    const { type, href } = linkConfig;
    const isInternalLink = type === 'internal' || href.charAt(0) === '/';
    if (isInternalLink) {
      // Internal link
      try {
        const testURL = new URL('http://my.marketplace.com' + href);
        const matchedRoutes = matchPathname(testURL.pathname, routeConfiguration);
        if (matchedRoutes.length > 0) {
          const found = matchedRoutes[0];
          const to = { search: testURL.search, hash: testURL.hash };
          return {
            ...linkConfig,
            route: {
              name: found.route?.name,
              params: found.params,
              to,
            },
          };
        }
      } catch (e) {
        return linkConfig;
      }
    }
    return linkConfig;
  });
};

const isCMSPage = found =>
  found.route?.name === 'CMSPage' ? `CMSPage:${found.params?.pageId}` : null;
const isInboxPage = found =>
  found.route?.name === 'InboxPage' ? `InboxPage:${found.params?.tab}` : null;
// Find the name of the current route/pathname.
// It's used as handle for currentPage check.
const getResolvedCurrentPage = (location, routeConfiguration) => {
  const matchedRoutes = matchPathname(location.pathname, routeConfiguration);
  if (matchedRoutes.length > 0) {
    const found = matchedRoutes[0];
    const cmsPageName = isCMSPage(found);
    const inboxPageName = isInboxPage(found);
    return cmsPageName ? cmsPageName : inboxPageName ? inboxPageName : `${found.route?.name}`;
  }
};

const GenericError = props => {
  const { show } = props;
  const classes = classNames(css.genericError, {
    [css.genericErrorVisible]: show,
  });
  return (
    <div className={classes} aria-hidden={!show}>
      <div className={css.genericErrorContent}>
        <p className={css.genericErrorText}>
          <FormattedMessage id="Topbar.genericError" />
        </p>
      </div>
    </div>
  );
};

const TopbarComponent = props => {
  const {
    className,
    rootClassName,
    desktopClassName,
    mobileRootClassName,
    mobileClassName,
    isAuthenticated,
    isLoggedInAs,
    authScopes = [],
    authInProgress,
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentPage,
    notificationCount = 0,
    totalUnreadConversations = 0,
    intl,
    history,
    location,
    onManageDisableScrolling,
    onResendVerificationEmail,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    showGenericError,
    config,
    routeConfiguration,
    isOnboardingForm = false,
    linkNotifications,
    onUpdateLinkedConsultant,
    onMarkLinkSeen,
    profileAccessRequests = [],
    integrityCompletionNotifications = [],
    onApproveAccessRequest,
    onRejectAccessRequest,
    onMarkIntegrityCompletionNotificationSeen,
    onMarkEmployerAnswerSeen,
    notices,
    onMarkNoticeSeen,
    profileListing,
  } = props;

  const handleSubmit = values => {
    const { currentSearchParams, history, location, config, routeConfiguration } = props;

    const topbarSearchParams = () => {
      if (isMainSearchTypeKeywords(config)) {
        return { keywords: values?.keywords };
      }
      // topbar search defaults to 'location' search
      const { search, selectedPlace } = values?.location;
      const { origin, bounds } = selectedPlace;
      const originMaybe = isOriginInUse(config) ? { origin } : {};

      return {
        ...originMaybe,
        address: search,
        bounds,
      };
    };
    const searchParams = {
      ...currentSearchParams,
      ...topbarSearchParams(),
      ...(values?.pub_listingType && { pub_listingType: values?.pub_listingType })
    };

    if (searchParams?.keywords == '') {
      delete searchParams.keywords;
    }

    // Note: For candidate listings, keywords are kept in URL and used for client-side
    // filtering only. SearchPage.duck.js removes them from API queries for candidate listings.

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, searchParams)
    );
  };

  const handleLogout = () => {
    const { onLogout, history, routeConfiguration } = props;
    onLogout().then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration);

      // In production we ensure that data is really lost,
      // but in development mode we use stored values for debugging
      if (appSettings.dev) {
        history.push(path);
      } else if (typeof window !== 'undefined') {
        window.location = path;
      }

      console.log('logged out'); // eslint-disable-line
    });
  };

  const { mobilemenu, mobilesearch, keywords, address, origin, bounds } = parse(location.search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  // Custom links are sorted so that group="primary" are always at the beginning of the list.
  const sortedCustomLinks = sortCustomLinks(config.topbar?.customLinks);
  const customLinks = getResolvedCustomLinks(sortedCustomLinks, routeConfiguration);
  const resolvedCurrentPage = currentPage || getResolvedCurrentPage(location, routeConfiguration);

  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;
  const isMobileMenuOpen = isMobileLayout && mobilemenu === 'open';
  const isMobileSearchOpen = isMobileLayout && mobilesearch === 'open';

  const mobileMenu = (
    <TopbarMobileMenu
      isAuthenticated={isAuthenticated}
      currentUserHasListings={currentUserHasListings}
      currentUser={currentUser}
      onLogout={handleLogout}
      notificationCount={notificationCount}
      totalUnreadConversations={totalUnreadConversations}
      currentPage={resolvedCurrentPage}
      customLinks={customLinks}
      isOnboardingForm={isOnboardingForm}
      profileListing={profileListing}
    />
  );

  const topbarSearcInitialValues = () => {
    if (isMainSearchTypeKeywords(config)) {
      return { keywords };
    }

    // Only render current search if full place object is available in the URL params
    const locationFieldsPresent = isOriginInUse(config)
      ? address && origin && bounds
      : address && bounds;
    return {
      location: locationFieldsPresent
        ? {
          search: address,
          selectedPlace: { address, origin, bounds },
        }
        : null,
    };
  };
  const initialSearchFormValues = topbarSearcInitialValues();

  const classes = classNames(rootClassName || css.root, className);

  const { display: searchFormDisplay = SEARCH_DISPLAY_ALWAYS } = config?.topbar?.searchBar || {};

  // Search form is shown conditionally depending on configuration and
  // the current page.
  const showSearchOnAllPages = searchFormDisplay === SEARCH_DISPLAY_ALWAYS;
  const showSearchOnSearchPage =
    searchFormDisplay === SEARCH_DISPLAY_ONLY_SEARCH_PAGE && resolvedCurrentPage === 'SearchPage';
  const showSearchNotOnLandingPage =
    searchFormDisplay === SEARCH_DISPLAY_NOT_LANDING_PAGE && resolvedCurrentPage !== 'LandingPage';

  const showSearchForm =
    showSearchOnAllPages || showSearchOnSearchPage || showSearchNotOnLandingPage;

  const mobileSearchButtonMaybe = showSearchForm ? (
    <Button
      rootClassName={css.searchMenu}
      onClick={() => redirectToURLWithModalState(history, location, 'mobilesearch')}
      title={intl.formatMessage({ id: 'Topbar.searchIcon' })}
    >
      <SearchIcon className={css.searchMenuIcon} />
    </Button>
  ) : (
    <div className={css.searchMenu} />
  );

  /* Notification Drawer */
  const [open, setOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  // Jobs the agency applied this candidate to on their behalf (consent pending),
  // shown inside the accept-invitation modal.
  const [appliedJobs, setAppliedJobs] = useState([]);
  const [appliedJobsLoading, setAppliedJobsLoading] = useState(false);

  const isCandidate = userTypeOf(currentUser) === 'candidate';
  // The candidate's current employer answered and the candidate has not opened it yet.
  const employerAnswerNotice = isCandidate ? employerAnswerNoticeOf(currentUser) : null;

  // When a recruiter (consultant invite) notification modal opens, fetch the
  // on-behalf apply transactions still awaiting this candidate's consent so the
  // candidate can see exactly which jobs they're approving by accepting.
  useEffect(() => {
    const data = selectedNotification?.data;
    const isRecruiterNotif = selectedNotification?.type === 'recruiter';
    // Candidate viewing their own invite: consultantId on the record is this
    // candidate's user id; fall back to currentUser id if absent.
    const candidateUserId = data?.consultantId || currentUser?.id?.uuid;
    // Agency's profile listing id from the invite record — matches the on-behalf
    // tx's agencyListingId (both derive from the agency's profileListingId).
    const agencyListingId = data?.recruiterListingId;

    if (!modalOpen || !isRecruiterNotif || !candidateUserId) {
      setAppliedJobs([]);
      return;
    }

    const payload = {
      type: 'fetch',
      consultantAdminId: candidateUserId,
      status: 'candidate consent pending',
      txType: 'apply',
      appliedByAgency: true,
      includeListingTitles: true,
    };
    // Scope to this agency when the record carries its listing id; otherwise
    // fetch every consent-pending on-behalf job for this candidate.
    if (agencyListingId) payload.agencyListingId = agencyListingId;

    let cancelled = false;
    setAppliedJobsLoading(true);
    manualTransactions(payload)
      .then(res => {
        if (cancelled) return;
        const list = Array.isArray(res) ? res : res?.transactions || [];
        setAppliedJobs(list);
      })
      .catch(() => {
        if (!cancelled) setAppliedJobs([]);
      })
      .finally(() => {
        if (!cancelled) setAppliedJobsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [modalOpen, selectedNotification]);

  const showDrawer = () => {
    setOpen(true);
  };
  const onClose = () => {
    setOpen(false);
  };

  // Handle opening the modal with notification details
  const handleNotificationClick = async (notification) => {
    if (notification?.type === 'notice') {
      const notice = notification.data || {};
      if (onMarkNoticeSeen) onMarkNoticeSeen(notice.id);
      onClose();
      if (notice.link) history.push(notice.link);
      return;
    }
    if (notification?.type === 'employerAnswer') {
      if (onMarkEmployerAnswerSeen) {
        await onMarkEmployerAnswerSeen(notification?.data?.answeredAt);
      }
      history.push(createResourceLocatorString('ProfileSettingsPage', routeConfiguration, {}, {}));
      setOpen(false);
      return;
    }

    if (notification?.type === 'integrityCompletion') {
      const data = notification?.data || {};

      if (onMarkIntegrityCompletionNotificationSeen && data?.notificationId) {
        await onMarkIntegrityCompletionNotificationSeen({
          notificationId: data.notificationId,
          candidateListingId: data.candidateListingId,
          listingId: data.listingId,
        });
      }

      const queryParams = data?.listingId ? { listingId: data.listingId } : {};
      history.push(
        createResourceLocatorString('JobPostsPage', routeConfiguration, { tab: 'applications' }, queryParams)
      );
      setOpen(false);
      setModalOpen(false);
      setSelectedNotification(null);
      return;
    }

    setSelectedNotification(notification);
    setModalOpen(true);
    setOpen(false); // Close drawer when opening modal
  };

  // Handle accept action
  const handleAccept = async () => {
    if (!selectedNotification || !onUpdateLinkedConsultant) return;

    const { data } = selectedNotification;

    let accepted = false;
    try {
      const result = await onUpdateLinkedConsultant({
        record: data,
        acceptRecord: true,
      });
      accepted = isAnswered(result);
    } catch (error) {
      console.error('Error accepting recruiter:', error);
    }

    if (!accepted) {
      message.error(intl.formatMessage({ id: 'TopbarDesktop.linkAcceptFailed' }));
    }

    setModalOpen(false);
    setSelectedNotification(null);
  };

  // Handle reject action
  const handleReject = async () => {
    if (!selectedNotification || !onUpdateLinkedConsultant) return;

    const { data } = selectedNotification;

    let declined = false;
    try {
      // The decline is kept on the link and the agency is told; the link is not removed.
      const result = await onUpdateLinkedConsultant({
        record: data,
        declineRecord: true,
      });
      declined = isAnswered(result);
    } catch (error) {
      console.error('Error rejecting recruiter:', error);
    }

    if (!declined) {
      message.error(intl.formatMessage({ id: 'TopbarDesktop.linkDeclineFailed' }));
    }

    setModalOpen(false);
    setSelectedNotification(null);
  };

  // Closing a notification: a consultant's answer is marked seen on its one link; nothing else
  // is saved.
  const handleMarkAsSeen = async () => {
    if (!selectedNotification) return;

    const { type, data } = selectedNotification;
    if (type === 'consultant' && data?._id && onMarkLinkSeen) {
      try {
        await onMarkLinkSeen({ _id: data._id });
      } catch (error) {
        console.error('Error marking notification as seen:', error);
      }
    }

    setModalOpen(false);
    setSelectedNotification(null);
  };

  // Handle approve profile access request
  const handleApproveProfileAccess = async () => {
    if (!selectedNotification || !onApproveAccessRequest) return;
    const { type, data } = selectedNotification;
    if (type !== 'profileAccess') return;

    let approved = false;
    try {
      const profileListingId = currentUser?.attributes?.profile?.publicData?.profileListingId || currentUser?.attributes?.profile?.publicData?.firstListingId;
      const profileListingSlug = data.profileListingSlug || currentUser?.attributes?.profile?.displayName?.toLowerCase().replace(/\s+/g, '-');
      const displayName = currentUser?.attributes?.profile?.displayName || `${currentUser?.attributes?.profile?.firstName} ${currentUser?.attributes?.profile?.lastName}`;

      const result = await onApproveAccessRequest({
        requestId: data._id,
        profileListingSlug,
        displayName,
      });
      approved = isAnswered(result);
    } catch (error) {
      console.error('Error approving access request:', error);
    }

    if (!approved) {
      message.error(intl.formatMessage({ id: 'TopbarDesktop.accessRequestFailed' }));
    }

    setModalOpen(false);
    setSelectedNotification(null);
  };

  // Handle reject profile access request
  const handleRejectProfileAccess = async () => {
    if (!selectedNotification || !onRejectAccessRequest) return;
    const { type, data } = selectedNotification;
    if (type !== 'profileAccess') return;

    let rejected = false;
    try {
      const profileOwnerName = currentUser?.attributes?.profile?.displayName || `${currentUser?.attributes?.profile?.firstName} ${currentUser?.attributes?.profile?.lastName}`;
      const profileTitle = 'Profile';

      const result = await onRejectAccessRequest({
        requestId: data._id,
        profileOwnerName,
        profileTitle,
      });
      rejected = isAnswered(result);
    } catch (error) {
      console.error('Error rejecting access request:', error);
    }

    if (!rejected) {
      message.error(intl.formatMessage({ id: 'TopbarDesktop.accessRequestFailed' }));
    }

    setModalOpen(false);
    setSelectedNotification(null);
  };

  // Generate notification items based on user type
  const getNotificationItems = () => {
    // The bell's notices come first
    const noticeItems = unseenNotices(notices).map((notice, index) => ({ data: notice, index, type: 'notice' }));

    // Profile access requests for all user types
    const accessRequestItems = (profileAccessRequests || []).map((request, index) => ({
      data: request,
      index,
      type: 'profileAccess'
    }));

    const integrityCompletionItems = (integrityCompletionNotifications || []).map((notification, index) => ({
      data: notification,
      index,
      type: 'integrityCompletion',
    }));

    if (isCandidate) {
      // For candidates: the link requests still waiting for their answer, newest first
      const pendingRequests = linkNotifications?.recruiterRequests || [];
      const recruiterItems = pendingRequests.map((recruiter, index) => ({
        recruiter,
        index,
        type: 'recruiter'
      }));
      const employerAnswerItems = employerAnswerNotice
        ? [{ data: employerAnswerNotice, index: 0, type: 'employerAnswer' }]
        : [];
      return [...noticeItems, ...accessRequestItems, ...employerAnswerItems, ...recruiterItems];
    } else {
      // For organisations: consultants' answers not yet marked seen, newest first
      const allConsultants = linkNotifications?.consultantAnswers || [];

      const consultantItems = allConsultants.map((consultant, index) => ({
        consultant,
        index,
        type: 'consultant'
      }));

      return [...noticeItems, ...accessRequestItems, ...integrityCompletionItems, ...consultantItems];
    }
  };

  const notificationItems = getNotificationItems();

  // Check if there are actual notifications
  const hasAccessRequestNotifications = (profileAccessRequests || []).length > 0;
  const hasIntegrityCompletionNotifications = (integrityCompletionNotifications || []).length > 0;
  const hasUnseenLinks = !!linkNotifications?.hasUnseen;
  const hasNotifications = unseenNotices(notices).length > 0 || hasAccessRequestNotifications || hasUnseenLinks || (isCandidate
    ? !!employerAnswerNotice
    : hasIntegrityCompletionNotifications);

  return (
    <div className={classes}>
      <LimitedAccessBanner
        isAuthenticated={isAuthenticated}
        isLoggedInAs={isLoggedInAs}
        authScopes={authScopes}
        currentUser={currentUser}
        onLogout={handleLogout}
        currentPage={resolvedCurrentPage}
      />
      <div className={classNames(mobileRootClassName || css.container, mobileClassName)}>
        <Button
          rootClassName={css.menu}
          onClick={() => redirectToURLWithModalState(history, location, 'mobilemenu')}
          title={intl.formatMessage({ id: 'Topbar.menuIcon' })}
        >
          <MenuIcon className={css.menuIcon} />
          {notificationDot}
        </Button>
        <LinkedLogo
          layout={'mobile'}
          alt={intl.formatMessage({ id: 'Topbar.logoIcon' })}
          linkToExternalSite={config?.topbar?.logoLink}
          currentUser={currentUser}
        />
        <div className={css.mobileMenuRight}>
          <Button
            rootClassName={css.notiMenu}
            onClick={showDrawer}
          >
            <Badge dot={hasNotifications}>
              <BellOutlined className={css.notiIcon} />
            </Badge>
          </Button>
          {mobileSearchButtonMaybe}
        </div>
      </div>
      <Drawer
        title="Notifications"
        closable={{ 'aria-label': 'Close Button' }}
        onClose={onClose}
        placement='left'
        open={open}
        className={css.notiDrawer}
      >
        {notificationItems.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
            No new notifications
          </div>
        ) : (
          <List
            dataSource={notificationItems}
            renderItem={(item) => {
              if (item.type === 'notice') {
                return (
                  <List.Item
                    onClick={() => handleNotificationClick({ type: 'notice', data: item.data })}
                    style={{ cursor: 'pointer', padding: '12px 0' }}
                  >
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <CheckCircleOutlined style={{ fontSize: '20px', color: '#0e9941' }} />
                      <div style={{ flex: 1 }}>
                        <NoticeContent notice={item.data} />
                      </div>
                      <RightOutlined style={{ fontSize: '14px', color: '#999' }} />
                    </div>
                  </List.Item>
                );
              }
              const isRecruiterNotif = item.type === 'recruiter';
              const isProfileAccessNotif = item.type === 'profileAccess';
              const isIntegrityCompletionNotif = item.type === 'integrityCompletion';
              const isEmployerAnswerNotif = item.type === 'employerAnswer';
              const data = isRecruiterNotif
                ? item.recruiter
                : isProfileAccessNotif || isIntegrityCompletionNotif || isEmployerAnswerNotif
                ? item.data
                : item.consultant;

              return (
                <List.Item
                  onClick={() => handleNotificationClick({ type: item.type, data })}
                  style={{ cursor: 'pointer', padding: '12px 0' }}
                >
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {isProfileAccessNotif && (
                      <UserAddOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
                    )}
                    {isIntegrityCompletionNotif && (
                      <CheckCircleOutlined style={{ fontSize: '20px', color: '#0e9941' }} />
                    )}
                    {!isRecruiterNotif && !isProfileAccessNotif && !isIntegrityCompletionNotif && (
                      <CheckCircleOutlined style={{ fontSize: '20px', color: '#52c41a' }} />
                    )}
                    <div style={{ flex: 1 }}>
                      {isProfileAccessNotif ? (
                        <>
                          <Text strong>{data?.firstName} {data?.lastName}</Text>
                          <Text> from </Text>
                          <Text strong>{data?.companyName}</Text>
                          <Text> requests profile access</Text>
                        </>
                      ) : isIntegrityCompletionNotif ? (
                        <>
                          <Text strong>{data?.candidateName || 'Candidate'}</Text>
                          <Text> completed the integrity check</Text>
                        </>
                      ) : isEmployerAnswerNotif ? (
                        <Text>{employerAnswerNoticeText(data)}</Text>
                      ) : (
                        <>
                          <Text strong>{isRecruiterNotif ? data?.recruiterName : data?.label || 'A consultant'}</Text>
                          <Text>
                            {isRecruiterNotif
                              ? ' has added you as a consultant'
                              : ' has responded to your request'}
                          </Text>
                        </>
                      )}
                      {!isRecruiterNotif && (
                        <div style={{ marginTop: '4px' }}>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {isIntegrityCompletionNotif
                              ? 'Click to view application'
                              : isEmployerAnswerNotif
                              ? 'Click to view your profile settings'
                              : 'Click to view details'}
                          </Text>
                        </div>
                      )}
                    </div>
                    <RightOutlined style={{ fontSize: '14px', color: '#999' }} />
                  </div>
                </List.Item>
              );
            }}
          />
        )}
      </Drawer>

      {/* Notification Modal */}
      <Modal
        id="NotificationModal"
        containerClassName={css.notificationModal}
        isOpen={modalOpen}
        onClose={() => {
          if (selectedNotification?.type === 'consultant') {
            handleMarkAsSeen();
          } else {
            setModalOpen(false);
            setSelectedNotification(null);
          }
        }}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        {selectedNotification && (
          <div style={{ padding: '20px' }}>
            <Title level={4} style={{ marginBottom: '16px' }}>
              {selectedNotification.type === 'profileAccess'
                ? `Profile Access Request from ${selectedNotification?.data?.firstName} ${selectedNotification?.data?.lastName}`
                : selectedNotification.type === 'recruiter'
                ? `${selectedNotification?.data?.recruiterName || 'A recruiter'} has added you as a consultant`
                : selectedNotification?.data?.status === 'left'
                ? intl.formatMessage({ id: 'Topbar.linkAnswerLeft' }, { name: selectedNotification?.data?.label || 'A consultant' })
                : `${selectedNotification?.data?.label || 'A consultant'} has ${selectedNotification?.data?.status === 'accepted' ? 'accepted' : 'declined'} your connection invitation`}
            </Title>

            {/* Profile Access Request Details */}
            {selectedNotification.type === 'profileAccess' && (
              <div style={{ marginTop: 16, marginBottom: 20 }}>
                <div style={{ marginBottom: 8 }}><strong>Name:</strong> {selectedNotification.data.firstName} {selectedNotification.data.lastName}</div>
                <div style={{ marginBottom: 8 }}><strong>Email:</strong> {selectedNotification.data.email}</div>
                <div style={{ marginBottom: 8 }}><strong>Company:</strong> {selectedNotification.data.companyName}</div>
                {selectedNotification.data.message && (
                  <div style={{ marginBottom: 8 }}>
                    <strong>Message:</strong>
                    <div style={{ marginTop: 4, padding: 8, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                      {selectedNotification.data.message}
                    </div>
                  </div>
                )}
                <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>
                  Requested on {new Date(selectedNotification.data.createdAt).toLocaleDateString()}
                </div>
              </div>
            )}

            {/* View Profile Link */}
            {selectedNotification?.type === 'recruiter' && selectedNotification.data.recruiterListingId && (
              <div style={{ marginBottom: '16px' }}>
                <NamedLink
                  name="ListingPage"
                  params={{
                    id: selectedNotification.data.recruiterListingId,
                    slug: selectedNotification.data.recruiterName?.toLowerCase().replace(/\s+/g, '-') || 'recruiter'
                  }}
                  style={{
                    color: '#1890ff',
                    textDecoration: 'underline',
                    fontSize: '14px'
                  }}
                >
                  View {selectedNotification.data.recruiterName || 'Recruiter'}'s Profile
                </NamedLink>
              </div>
            )}

            {selectedNotification?.type === 'recruiter' && (
              <div style={{ marginBottom: '16px' }}>
                <NamedLink name="MyNetworkPage">{intl.formatMessage({ id: 'Topbar.openMyNetwork' })}</NamedLink>
              </div>
            )}

            {/* Info about where this will be displayed */}
            {selectedNotification.type === 'profileAccess' ? null : (
              <div style={{
                backgroundColor: '#f5f5f5',
                padding: '12px',
                borderRadius: '4px',
                marginBottom: '20px'
              }}>
                <Text type="secondary" style={{ fontSize: '13px' }}>
                  {selectedNotification.type === 'recruiter' ? (
                    <>
                      By accepting, <strong>{selectedNotification?.data?.recruiterName || 'this recruiter'}</strong> will be displayed as your linked Suppliers in your profile under the <strong>Current Employer</strong> section.
                    </>
                  ) : selectedNotification?.type == 'profileAccess' ? null :(
                    <>
                      { selectedNotification?.data?.status === 'left'
                        ? intl.formatMessage({ id: 'Topbar.linkAnswerLeftDetail' }, { name: selectedNotification?.data?.label })
                        : selectedNotification?.data?.status === 'accepted'
                        ? `${selectedNotification?.data?.label} has accepted your connection invitation and will now be visible in your profile.`
                        : `${selectedNotification?.data?.label} has declined your connection invitation and will not appear in your profile.`}
                    </>
                  )}
                </Text>
              </div>
            )}

            {/* Jobs the agency applied this candidate to (consent gate) */}
            {selectedNotification.type === 'recruiter' && (appliedJobsLoading || appliedJobs.length > 0) && (
              <div style={{
                border: '1px solid #ffe7ba',
                backgroundColor: '#fffbe6',
                padding: '12px',
                borderRadius: '4px',
                marginBottom: '20px'
              }}>
                <Text strong style={{ fontSize: '13px', display: 'block', marginBottom: 8 }}>
                  {appliedJobsLoading
                    ? 'Checking jobs applied on your behalf…'
                    : `${selectedNotification?.data?.recruiterName || 'This supplier'} has applied you to ${appliedJobs.length} job${appliedJobs.length === 1 ? '' : 's'} on your behalf:`}
                </Text>
                {!appliedJobsLoading && (
                  <ul style={{ margin: '0 0 8px 18px', padding: 0 }}>
                    {appliedJobs.map(job => (
                      <li key={job.transactionId} style={{ fontSize: '13px', color: '#484848' }}>
                        {job.listingTitle || 'Job application'}
                      </li>
                    ))}
                  </ul>
                )}
                {!appliedJobsLoading && (
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    By accepting, you approve every job this supplier has applied you to on your behalf.
                  </Text>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              {selectedNotification?.type === 'profileAccess' ? (
                <>
                  <Button
                    onClick={handleRejectProfileAccess}
                    style={{
                      flex: '1 1 auto',
                      minWidth: '120px',
                      borderColor: '#ff4d4f',
                      color: '#ff4d4f'
                    }}
                  >
                    Reject
                  </Button>
                  <Button
                    onClick={handleApproveProfileAccess}
                    style={{
                      flex: '1 1 auto',
                      minWidth: '120px',
                      backgroundColor: '#0e9941',
                      borderColor: '#0e9941',
                      color: 'white'
                    }}
                  >
                    Approve
                  </Button>
                </>
              ) : selectedNotification?.type === 'recruiter' ? (
                <>
                  <Button
                    onClick={handleReject}
                    style={{
                      flex: '1 1 auto',
                      minWidth: '120px',
                      borderColor: '#ff4d4f',
                      color: '#ff4d4f'
                    }}
                  >
                    Reject
                  </Button>
                  <Button
                    onClick={handleAccept}
                    style={{
                      flex: '1 1 auto',
                      minWidth: '120px',
                      backgroundColor: '#1890ff',
                      borderColor: '#1890ff',
                      color: 'white'
                    }}
                  >
                    Accept
                  </Button>
                </>
              ) : (
                <Button
                  onClick={handleMarkAsSeen}
                  style={{
                    flex: '1 1 auto',
                    minWidth: '120px',
                    backgroundColor: '#1890ff',
                    borderColor: '#1890ff',
                    color: 'white'
                  }}
                >
                  Close
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>
      <div className={css.desktop}>
        <TopbarDesktop
          className={desktopClassName}
          currentUserHasListings={currentUserHasListings}
          currentUser={currentUser}
          currentPage={resolvedCurrentPage}
          initialSearchFormValues={initialSearchFormValues}
          intl={intl}
          isAuthenticated={isAuthenticated}
          notificationCount={notificationCount}
          onLogout={handleLogout}
          onSearchSubmit={handleSubmit}
          config={config}
          customLinks={customLinks}
          showSearchForm={showSearchForm}
          isOnboardingForm={isOnboardingForm}
          linkNotifications={linkNotifications}
          onUpdateLinkedConsultant={onUpdateLinkedConsultant}
          onMarkLinkSeen={onMarkLinkSeen}
          profileAccessRequests={profileAccessRequests}
          integrityCompletionNotifications={integrityCompletionNotifications}
          onApproveAccessRequest={onApproveAccessRequest}
          onRejectAccessRequest={onRejectAccessRequest}
          onMarkIntegrityCompletionNotificationSeen={onMarkIntegrityCompletionNotificationSeen}
          onMarkEmployerAnswerSeen={onMarkEmployerAnswerSeen}
          notices={notices}
          onMarkNoticeSeen={onMarkNoticeSeen}
          history={history}
          routeConfiguration={routeConfiguration}
          profileListing={profileListing}
        />
      </div>
      <Modal
        id="TopbarMobileMenu"
        containerClassName={css.modalContainer}
        isOpen={isMobileMenuOpen}
        onClose={() => redirectToURLWithoutModalState(history, location, 'mobilemenu')}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        {authInProgress ? null : mobileMenu}
      </Modal>
      <Modal
        id="TopbarMobileSearch"
        containerClassName={css.modalContainerSearchForm}
        isOpen={isMobileSearchOpen}
        onClose={() => redirectToURLWithoutModalState(history, location, 'mobilesearch')}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <p className={css.mobileHelp}>
          <FormattedMessage id="Topbar.mobileSearchHelp" />
        </p>
        <div className={css.searchContainer}>
          <TopbarSearchForm
            onSubmit={handleSubmit}
            initialValues={initialSearchFormValues}
            isMobile
            appConfig={config}
            currentUser={currentUser}
            pageLocation={location}
            profileListing={profileListing}
          />
        </div>
      </Modal>
      <ModalMissingInformation
        id="MissingInformationReminder"
        containerClassName={css.missingInformationModal}
        currentUser={currentUser}
        currentUserHasListings={currentUserHasListings}
        currentUserHasOrders={currentUserHasOrders}
        location={location}
        onManageDisableScrolling={onManageDisableScrolling}
        onResendVerificationEmail={onResendVerificationEmail}
        sendVerificationEmailInProgress={sendVerificationEmailInProgress}
        sendVerificationEmailError={sendVerificationEmailError}
      />

      <GenericError show={showGenericError} />
    </div>
  );
};

/**
 * Topbar containing logo, main search and navigation links.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.desktopClassName add more style rules for TopbarDesktop
 * @param {Object} props.mobileRootClassName overwrite mobile layout root classes
 * @param {Object} props.mobileClassName add more style rules for mobile layout
 * @param {boolean} props.isAuthenticated
 * @param {boolean} props.isLoggedInAs
 * @param {Object} props.currentUser
 * @param {boolean} props.currentUserHasListings
 * @param {boolean} props.currentUserHasOrders
 * @param {string} props.currentPage
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onManageDisableScrolling
 * @param {Function} props.onResendVerificationEmail
 * @param {Object} props.sendVerificationEmailInProgress
 * @param {Object} props.sendVerificationEmailError
 * @param {boolean} props.showGenericError
 * @param {Object} props.history
 * @param {Function} props.history.push
 * @param {Object} props.location
 * @param {string} props.location.search '?foo=bar'
 * @returns {JSX.Element} topbar component
 */
const Topbar = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  return (
    <TopbarComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      {...props}
    />
  );
};

export default Topbar;
